import React, { useState } from 'react';
import { Wrench, Zap, Paintbrush, CheckCircle, Settings, Hammer } from 'lucide-react';

const Services: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'mechanical' | 'performance' | 'bodywork'>('mechanical');

  const tabs = [
    { id: 'mechanical', label: 'Mecânica Geral', icon: <Wrench className="h-5 w-5" /> },
    { id: 'performance', label: 'Performance', icon: <Zap className="h-5 w-5" /> },
    { id: 'bodywork', label: 'Funilaria', icon: <Paintbrush className="h-5 w-5" /> },
  ];

  const mechanicalServices = [
    'Manutenção preventiva - Realização de inspeções regulares e serviços preventivos para evitar problemas mecânicos futuros',
    'Reparação mecânica - Diagnóstico e correção de problemas mecânicos existentes no veículo',
    'Serviços elétricos e eletrônicos - Diagnóstico e reparo de falhas nos sistemas elétricos e eletrônicos',
    'Serviços de pneus - Troca, reparo e balanceamento de pneus para garantir uma condução segura',
    'Serviços de ar condicionado - Manutenção e reparo do sistema de ar condicionado',
    'Serviços de inspeção e emissão de gases - Realização de inspeções veiculares e testes de emissão',
    'Serviços de suspensão - Ajuste, reparo e substituição de componentes do sistema de suspensão',
    'Serviços de transmissão - Reparação e manutenção da transmissão do veículo'
  ];

  const performanceServices = [
    {
      title: 'Personalização de Motores',
      description: 'Modificação e ajuste do motor para melhorar desempenho, potência e eficiência de acordo com suas necessidades'
    },
    {
      title: 'Reprogramação da ECU',
      description: 'Modificação do software da ECU para ajustar parâmetros de combustão, injeção e temporização'
    },
    {
      title: 'Instalação de Chips de Desempenho',
      description: 'Dispositivos eletrônicos que otimizam o desempenho do motor, aumentando potência e eficiência'
    },
    {
      title: 'Melhorias no Sistema de Admissão e Escape',
      description: 'Instalação de filtros esportivos e sistemas de escape para melhorar a eficiência do motor'
    },
    {
      title: 'Turbo ou Supercharger',
      description: 'Instalação de turbocompressor ou supercharger para aumentar significativamente a potência'
    },
    {
      title: 'Atualização de Componentes Internos',
      description: 'Substituição de pistões, bielas, virabrequim e válvulas para suportar maior desempenho'
    },
    {
      title: 'Suspensão Esportiva',
      description: 'Melhoria do sistema de suspensão para maior estabilidade e precisão em curvas'
    },
    {
      title: 'Troca de Rodas e Pneus',
      description: 'Utilização de rodas leves e pneus de alta performance para melhorar tração'
    },
    {
      title: 'Freios de Alto Desempenho',
      description: 'Instalação de sistemas de freios mais eficientes com discos maiores e pinças esportivas'
    },
    {
      title: 'Ajustes e Calibrações',
      description: 'Ajustes finos em diversos componentes para otimizar o desempenho geral'
    },
    {
      title: 'Dinamômetro',
      description: 'Equipamento para medir a potência real do motor e ajustar as modificações'
    }
  ];

  const bodyworkServices = [
    {
      title: 'Reparos em Colisões',
      description: 'Correção de danos causados por colisões, incluindo amassados, rachaduras e deformações'
    },
    {
      title: 'Substituição de Peças Danificadas',
      description: 'Remoção e substituição de peças muito danificadas por novas ou usadas em boas condições'
    },
    {
      title: 'Retoques e Pintura',
      description: 'Preparação da superfície e pintura com combinação da cor original do veículo'
    },
    {
      title: 'Reparos em Partes Estruturais',
      description: 'Endireitamento e alinhamento da estrutura do chassi com equipamentos específicos'
    },
    {
      title: 'Reparação de Partes Plásticas',
      description: 'Trabalho com partes plásticas como para-choques e painéis de acabamento'
    },
    {
      title: 'Soldagem',
      description: 'Técnicas de soldagem para unir peças metálicas e fazer reparos em áreas danificadas'
    },
    {
      title: 'Alinhamento e Nivelamento',
      description: 'Verificação e ajuste do alinhamento das peças da carroceria'
    }
  ];

  const renderMechanicalContent = () => (
    <div className="animate-fadeIn">
      <div className="text-center mb-16">
        <div className="flex justify-center mb-6">
          <div className="bg-primary-100 p-4 rounded-full">
            <Wrench className="h-12 w-12 text-primary-600" />
          </div>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
          Mecânica Automotiva
        </h2>
        <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
          Uma oficina mecânica automotiva é um estabelecimento especializado na manutenção, 
          reparação e serviços relacionados a veículos automóveis. O principal objetivo da oficina 
          é garantir que os veículos estejam em boas condições de funcionamento, segurança e desempenho.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {mechanicalServices.map((service, index) => (
          <div key={index} className="flex items-start space-x-4 p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
            <CheckCircle className="h-6 w-6 text-primary-600 flex-shrink-0 mt-1" />
            <p className="text-gray-700 leading-relaxed">{service}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 p-8 bg-primary-50 rounded-lg">
        <p className="text-lg text-gray-700 leading-relaxed">
          <strong>Os técnicos e mecânicos da Will Performance</strong> são profissionais treinados e qualificados 
          para lidar com uma ampla variedade de problemas mecânicos e elétricos. Eles utilizam ferramentas 
          especializadas e equipamentos para realizar os serviços com precisão e eficiência.
        </p>
        <p className="text-lg text-gray-700 leading-relaxed mt-4">
          <strong>A Will Performance Mecânica automotiva</strong> desempenha um papel crucial na manutenção 
          e reparação de veículos, ajudando a prolongar sua vida útil, garantir sua segurança nas estradas 
          e manter um bom desempenho geral.
        </p>
      </div>
    </div>
  );

  const renderPerformanceContent = () => (
    <div className="animate-fadeIn">
      <div className="text-center mb-16">
        <div className="flex justify-center mb-6">
          <div className="bg-primary-100 p-4 rounded-full">
            <Zap className="h-12 w-12 text-primary-600" />
          </div>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
          Performance Automotiva
        </h2>
        <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
          Um serviço de performance automotiva é especializado em melhorar o desempenho de carros, 
          tornando-os mais rápidos, ágeis, potentes e eficientes. Esses serviços são procurados por 
          muitos apaixonados por motores que desejam maximizar o potencial de seus veículos.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {performanceServices.map((service, index) => (
          <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center mb-4">
              <div className="bg-primary-100 p-2 rounded-lg mr-3">
                <Settings className="h-6 w-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                {service.title}
              </h3>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {service.description}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-12 p-8 bg-red-50 border-l-4 border-red-400">
        <p className="text-lg text-gray-700 leading-relaxed">
          <strong>É importante mencionar</strong> que as modificações de desempenho devem ser feitas com cuidado 
          e por profissionais experientes, pois alterações inadequadas podem causar danos ao veículo e comprometer 
          sua segurança. Além disso, algumas modificações podem invalidar a garantia do fabricante do carro. 
          Portanto, é essencial procurar um serviço de desempenho confiável e conhecedor para realizar essas 
          alterações de forma segura e eficaz.
        </p>
      </div>
    </div>
  );

  const renderBodyworkContent = () => (
    <div className="animate-fadeIn">
      <div className="text-center mb-16">
        <div className="flex justify-center mb-6">
          <div className="bg-primary-100 p-4 rounded-full">
            <Paintbrush className="h-12 w-12 text-primary-600" />
          </div>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
          Funilaria Automotiva
        </h2>
        <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
          Funilaria Will Performance especializada na reparação e restauração de carrocerias de veículos automotores. 
          É importante destacar que o serviço de funilaria automotiva trabalha exclusivamente com a parte externa 
          do veículo, lidando com a sua estética e estrutura da carroceria.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {bodyworkServices.map((service, index) => (
          <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center mb-4">
              <div className="bg-primary-100 p-2 rounded-lg mr-3">
                <Hammer className="h-6 w-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                {service.title}
              </h3>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {service.description}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center">
        <div className="bg-primary-50 p-8 rounded-lg">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Na Will Performance cuidamos de tudo para você!
          </h3>
          <p className="text-lg text-gray-700 leading-relaxed">
            Nossa equipe especializada está pronta para atender todas as suas necessidades 
            automotivas com qualidade e dedicação.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              NOSSOS
              <span className="block text-yellow-400">SERVIÇOS</span>
            </h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed">
              Soluções completas para seu veículo com qualidade e preços justos
            </p>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="bg-white shadow-lg sticky top-20 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-center">
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center justify-center rounded-lg font-medium transition-all duration-300 relative overflow-hidden ${
                    activeTab === tab.id
                      ? 'bg-primary-600 text-white shadow-md'
                      : 'text-gray-600 hover:text-primary-600 hover:bg-white'
                  } ${
                    activeTab === tab.id 
                      ? 'px-4 sm:px-6 py-3' 
                      : 'w-12 h-12 sm:w-auto sm:px-6 sm:py-3'
                  }`}
                >
                  <span className="flex-shrink-0">{tab.icon}</span>
                  <span className={`transition-all duration-300 overflow-hidden whitespace-nowrap ${
                    activeTab === tab.id 
                      ? 'ml-2 opacity-100 max-w-xs' 
                      : 'ml-0 opacity-0 max-w-0 sm:ml-2 sm:opacity-100 sm:max-w-xs'
                  }`}>
                    {tab.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'mechanical' && renderMechanicalContent()}
          {activeTab === 'performance' && renderPerformanceContent()}
          {activeTab === 'bodywork' && renderBodyworkContent()}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Precisa de algum desses serviços?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Entre em contato conosco e solicite um orçamento. Nossa equipe está pronta 
            para atender você com qualidade e preços justos.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://api.whatsapp.com/send?phone=551123593715"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 transform hover:scale-105"
            >
              Solicitar Orçamento
            </a>
            <a
              href="tel:+551123593715"
              className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 transform hover:scale-105"
            >
              Ligar Agora
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
